import { isObject, isFunction, isProduction } from './helpers';

const createErrorMessage = (styleSheetName, key, seed) => {
  return `
Invalid Function-rule found in stylesheet "${styleSheetName}" at key "${key}" with the seed "${seed}".
Using a seed with combination of a Function-rule is not supported, prefer using a Function-value.

* Function rule (BAD)
  -------------------
  root: props => {
    color: props.something ? "red" : "green" 
  } 

* Function value (GOOD)
  ---------------------
  root: {
    color: props => props.something ? "red" : "green" 
  }
  
* Read more about Function-rules and Function-values in the JSS docs:
  https://cssinjs.org/jss-plugin-rule-value-function/?v=v10.9.1-alpha.2
  `;
};

/**
 * This function will add a seed to every '.Mui' selector in your styles object
 *
 * Mui is adding a seed to every CSS selector it created, but not to nested selectors that consumers declare.
 *
 * For example, this nested Mui selector won't get a seed:
 *
 * ```
 * root: {
 *  '& .MuiButton':{ // <- this won't get a seed by mui
 *
 *   }
 * }
 * ```
 *
 * The only exception are the Mui state selector (e.g .Mui-checked, .Mui-hover) because mui isn't attaching the seed at all and they are always chained to a different mui element anyway.
 *
 * E.g `.MuiButton.Mui-hover`
 *
 * @param {{}} style - The target style object
 * @param {string} seed  - The seed value you want to inject
 * @param {{}} metaData - Some extra info about the function (for debugging experience)
 * @param {number} depth - Internal use to ignore rule functions
 * @returns object
 */
const injectSeedToStyles = (style, seed, metaData = {}, depth = -1) => {
  if (!seed) {
    return style;
  }

  depth += 1;
  const { name, classNamePrefix } = metaData;
  const styleSheetName = name || classNamePrefix || 'makeStyles (default name)';
  let themeWithSeed = {};

  Object.entries(style).forEach(([key, value]) => {
    let newKey = key;
    const isMuiSelector = key.includes('.Mui');
    const isMuiStateSelector = key.includes('.Mui-');
    // We shouldn't modify mui state selectors (e.g .Mui-hover, .Mui-checked) because MUI is not overriding them as well.
    if (isMuiSelector && !isMuiStateSelector) {
      newKey = key.replaceAll('.Mui', `.${seed}-Mui`);
    }

    if (isObject(value)) {
      themeWithSeed[newKey] = injectSeedToStyles(value, seed, metaData, depth);
    } else if (depth === 0 && isFunction(value)) {
      // We don't support seed & rule functions because we can't replace .Mui* selectors inside a function's body.
      // We only check the top level of the styles object because in nested level these functions are probably a value functions and not a rule functions.
      const errorMsg = createErrorMessage(styleSheetName, key, seed);
      if (isProduction) {
        console.error(errorMsg);
      } else {
        throw new Error(errorMsg);
      }
    } else {
      themeWithSeed[newKey] = value;
    }
  });

  return themeWithSeed;
};

export default injectSeedToStyles;
